import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTableElement from "../../../components/elements/DataTableElement";
import CustomButton from "../../../components/form/CustomButton";
import CustomInput from "../../../components/form/CustomInput";
import {
    invoiceLoads,
    invoicesResource,
} from "../../../helpers/configRoutes/backend_helper";
import { currencyUSD } from "../../../helpers/forms/formater";
import { ToastHelper } from "../../../helpers/configRoutes/alerts/alertHelper";

const emptyLoading = { show: false, textLoad: "Loading..." };

const Accounting = () => {
    const navigate = useNavigate();

    const [filters, setFilters] = useState({ search: "", page: 1, perPage: 50 });
    const [tableLoad, setTableLoad] = useState({ show: false, textLoad: "Loading invoiceable loads..." });
    const [invoiceLoadRows, setInvoiceLoadRows] = useState([]);
    const [loadMeta, setLoadMeta] = useState({ total: 0, current_page: 0 });

    const [invoiceHistory, setInvoiceHistory] = useState([]);
    const [historyLoad, setHistoryLoad] = useState({ show: false, textLoad: "Loading invoices..." });

    const [selectedLoads, setSelectedLoads] = useState([]);
    const [invoiceForm, setInvoiceForm] = useState({
        invoice_date: "",
        due_date: "",
        tax: "0",
        notes: "",
    });
    const [saving, setSaving] = useState(false);

    const selectedClient = selectedLoads[0]?.client || null;
    const selectedLoadIds = useMemo(() => selectedLoads.map((load) => load.id), [selectedLoads]);
    const selectedSubtotal = useMemo(
        () => selectedLoads.reduce((sum, load) => sum + (parseFloat(load.charges) || 0), 0),
        [selectedLoads]
    );
    const selectedTax = parseFloat(invoiceForm.tax) || 0;
    const selectedTotal = selectedSubtotal + selectedTax;

    const refreshLoads = async () => {
        setTableLoad({ show: true, textLoad: "Loading invoiceable loads..." });
        const response = await invoiceLoads({ params: filters });
        setInvoiceLoadRows(response?.loads || []);
        setLoadMeta(response?.meta || { total: 0, current_page: 0 });
        setTableLoad(emptyLoading);
    };

    const refreshInvoices = async () => {
        setHistoryLoad({ show: true, textLoad: "Loading invoices..." });
        const response = await invoicesResource("get", { params: { perPage: 20 } });
        setInvoiceHistory(response?.invoices || []);
        setHistoryLoad(emptyLoading);
    };

    useEffect(() => {
        refreshLoads();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    useEffect(() => {
        refreshInvoices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isSelected = (loadId) => selectedLoadIds.includes(loadId);

    const handleLoadToggle = (load) => {
        if (isSelected(load.id)) {
            setSelectedLoads((prev) => prev.filter((item) => item.id !== load.id));
            return;
        }

        if (selectedClient && Number(load.client_id) !== Number(selectedClient.id)) {
            ToastHelper("You can only select loads from the same client for one invoice.", "error");
            return;
        }

        setSelectedLoads((prev) => [...prev, load]);
    };

    const clearSelection = () => {
        setSelectedLoads([]);
        setInvoiceForm({ invoice_date: "", due_date: "", tax: "0", notes: "" });
    };

    const createInvoice = async () => {
        if (!selectedClient || selectedLoadIds.length === 0) {
            ToastHelper("Select at least one load to create an invoice.", "error");
            return;
        }

        setSaving(true);
        const response = await invoicesResource("post", {
            client_id: selectedClient.id,
            load_ids: selectedLoadIds,
            invoice_date: invoiceForm.invoice_date || undefined,
            due_date: invoiceForm.due_date || undefined,
            tax: selectedTax,
            notes: invoiceForm.notes || undefined,
        });

        if (response?.status) {
            clearSelection();
            await Promise.all([refreshLoads(), refreshInvoices()]);
        }
        setSaving(false);
    };

    const updateInvoiceStatus = async (invoice, status) => {
        const response = await invoicesResource("put", { status }, invoice.id);
        if (response?.status && response.invoice) {
            setInvoiceHistory((prev) => prev.map((item) => item.id === invoice.id ? response.invoice : item));
        }
    };

    const loadColumns = [
        {
            id: "select",
            name: "",
            cell: (row) => {
                const disabled = selectedClient && Number(row.client_id) !== Number(selectedClient.id);
                return (
                    <input
                        type="checkbox"
                        checked={isSelected(row.id)}
                        disabled={disabled}
                        onChange={() => handleLoadToggle(row)}
                        title={disabled ? "This load belongs to another client" : "Select load"}
                        className="h-4 w-4 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                    />
                );
            },
        },
        {
            id: "client_work_order",
            name: "CLIENT WORK ORDER",
            cell: (row) => <span className="font-semibold text-cs-dark">{row.client_work_order || "-"}</span>,
        },
        {
            id: "client",
            name: "CLIENT",
            cell: (row) => (
                <div className="flex flex-col">
                    <span className="font-medium text-cs-dark">{row.client?.company_name || "-"}</span>
                    <span className="text-[10px] text-cs-muted">{row.client?.contact_name || ""}</span>
                </div>
            ),
        },
        {
            id: "dates",
            name: "DATES",
            cell: (row) => (
                <div className="text-[11px] text-cs-muted">
                    <div>PU: {row.pick_up_date || "-"}</div>
                    <div>DEL: {row.delivery_date || "-"}</div>
                </div>
            ),
        },
        {
            id: "amount",
            name: "AMOUNT",
            cell: (row) => <span className="font-semibold text-cs-primary-dark">{currencyUSD(row.charges || 0)}</span>,
        },
        {
            id: "status",
            name: "STATUS",
            cell: (row) => <span className="text-[11px] text-cs-muted">{row.status?.name || "-"}</span>,
        },
    ];

    const invoiceColumns = [
        {
            id: "invoice_number",
            name: "INVOICE",
            cell: (row) => <span className="font-mono font-semibold text-cs-dark">{row.invoice_number}</span>,
        },
        {
            id: "client",
            name: "CLIENT",
            cell: (row) => <span className="font-medium text-cs-dark">{row.client_name || "-"}</span>,
        },
        {
            id: "loads",
            name: "LOADS",
            cell: (row) => <span>{row.loads?.length || 0}</span>,
        },
        {
            id: "total",
            name: "TOTAL",
            cell: (row) => <span className="font-semibold text-cs-primary-dark">{currencyUSD(row.total || 0)}</span>,
        },
        {
            id: "status",
            name: "STATUS",
            cell: (row) => (
                <select
                    value={row.status || "Unpaid"}
                    onChange={(event) => updateInvoiceStatus(row, event.target.value)}
                    className="px-2 py-1 rounded-lg border border-cs-border bg-cs-bg text-xs"
                >
                    <option value="Unpaid">Unpaid</option>
                    <option value="Paid">Paid</option>
                </select>
            ),
        },
        {
            id: "actions",
            name: "",
            cell: (row) => (
                <button
                    type="button"
                    onClick={() => navigate(`/payment-details/${row.id}`)}
                    className="px-3 py-1.5 rounded-full text-xs bg-cs-primary-soft text-cs-primary-dark hover:bg-cs-primary hover:text-white transition-colors"
                >
                    Details
                </button>
            ),
        },
    ];

    return (
        <div className="min-h-screen bg-cs-bg-alt py-8 px-4">
            <div className="max-w-auto mx-auto space-y-5">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.18em] text-cs-muted">Billing center</p>
                        <h1 className="text-2xl font-semibold text-cs-dark">Invoices</h1>
                        <p className="text-sm text-cs-muted mt-1">
                            Select finished or completed loads to invoice. One invoice can only include loads from the same client.
                        </p>
                    </div>
                    <div className="text-[11px] text-cs-muted">
                        Invoiceable loads: <span className="font-semibold text-cs-dark">{loadMeta.total || invoiceLoadRows.length}</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5">
                    <div className="bg-cs-bg border border-cs-border rounded-2xl shadow-sm overflow-hidden">
                        <div className="p-4 border-b border-cs-border flex flex-col md:flex-row md:items-end gap-3 justify-between">
                            <div>
                                <h2 className="text-sm font-semibold text-cs-dark">Select loads to invoice</h2>
                                <p className="text-xs text-cs-muted mt-1">
                                    After selecting the first load, loads from other clients are disabled automatically.
                                </p>
                            </div>
                            <CustomInput
                                label="Search"
                                type="text"
                                name="search"
                                value={filters.search}
                                placeholder="Client, load ID or client work order"
                                labelClassName="text-xs font-medium text-cs-dark"
                                handleChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value, page: 1 }))}
                                className="bg-cs-bg-alt border-cs-border"
                            />
                        </div>
                        <DataTableElement columns={loadColumns} items={invoiceLoadRows} tableLoad={tableLoad} />
                    </div>

                    <div className="bg-cs-bg border border-cs-border rounded-2xl shadow-sm p-4 space-y-4 h-fit sticky top-4">
                        <div>
                            <h2 className="text-sm font-semibold text-cs-dark">Invoice draft</h2>
                            <p className="text-xs text-cs-muted mt-1">
                                {selectedClient ? `Client: ${selectedClient.company_name}` : "No client selected yet."}
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <CustomInput
                                label="Invoice date"
                                type="date"
                                name="invoice_date"
                                value={invoiceForm.invoice_date}
                                labelClassName="text-xs font-medium text-cs-dark"
                                handleChange={(event) => setInvoiceForm((prev) => ({ ...prev, invoice_date: event.target.value }))}
                                className="bg-cs-bg-alt border-cs-border"
                            />
                            <CustomInput
                                label="Due date"
                                type="date"
                                name="due_date"
                                value={invoiceForm.due_date}
                                labelClassName="text-xs font-medium text-cs-dark"
                                handleChange={(event) => setInvoiceForm((prev) => ({ ...prev, due_date: event.target.value }))}
                                className="bg-cs-bg-alt border-cs-border"
                            />
                        </div>

                        <CustomInput
                            label="Tax"
                            type="number"
                            name="tax"
                            value={invoiceForm.tax}
                            min="0"
                            step="0.01"
                            labelClassName="text-xs font-medium text-cs-dark"
                            handleChange={(event) => setInvoiceForm((prev) => ({ ...prev, tax: event.target.value }))}
                            className="bg-cs-bg-alt border-cs-border"
                        />

                        <label className="flex flex-col gap-1 text-xs font-medium text-cs-dark">
                            Notes
                            <textarea
                                name="notes"
                                value={invoiceForm.notes}
                                onChange={(event) => setInvoiceForm((prev) => ({ ...prev, notes: event.target.value }))}
                                className="min-h-[80px] rounded-md border border-cs-border bg-cs-bg-alt p-3 text-sm font-normal outline-none focus:ring-2 focus:ring-cs-primary/30"
                                placeholder="Optional notes"
                            />
                        </label>

                        <div className="rounded-xl bg-cs-bg-alt border border-cs-border p-3 text-xs space-y-2">
                            <div className="flex justify-between"><span>Selected loads</span><strong>{selectedLoads.length}</strong></div>
                            <div className="flex justify-between"><span>Subtotal</span><strong>{currencyUSD(selectedSubtotal)}</strong></div>
                            <div className="flex justify-between"><span>Tax</span><strong>{currencyUSD(selectedTax)}</strong></div>
                            <div className="flex justify-between text-sm text-cs-dark"><span>Total</span><strong>{currencyUSD(selectedTotal)}</strong></div>
                        </div>

                        <div className="flex gap-2">
                            <CustomButton
                                type="button"
                                onClick={createInvoice}
                                loading={saving}
                                textLoad="Creating..."
                                disabled={!selectedLoads.length || saving}
                                className="flex-1 px-4 py-2.5 rounded-xl bg-cs-primary text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Create invoice
                            </CustomButton>
                            <button
                                type="button"
                                onClick={clearSelection}
                                className="px-4 py-2.5 rounded-xl border border-cs-border text-sm text-cs-muted hover:bg-cs-bg-alt"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                <div className="bg-cs-bg border border-cs-border rounded-2xl shadow-sm overflow-hidden">
                    <div className="p-4 border-b border-cs-border">
                        <h2 className="text-sm font-semibold text-cs-dark">Invoice history</h2>
                        <p className="text-xs text-cs-muted mt-1">Created customer invoices and payment status.</p>
                    </div>
                    <DataTableElement columns={invoiceColumns} items={invoiceHistory} tableLoad={historyLoad} />
                </div>
            </div>
        </div>
    );
};

export default Accounting;
